import type { ImageMetadata } from '../../types/image';
import type { SortingOptions } from './StudySorter';
import type {
  Ocg19InstanceInfo,
  OrthancSeriesInfo,
} from '@/src/core/orthanc-api';

import vtkDataArray from '@kitware/vtk.js/Common/Core/DataArray';
import vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';
// !TODO: fix this
import { defineStore } from 'pinia';
import { markRaw } from 'vue';
import z from 'zod';

import { getLPSDirections } from '../../utils/lps';

import { useViewportStateStore } from '../contours/viewport-state-store';
import {
  defaultImageMetadata,
  VtkImageAndMetaDataCache,
} from './current-study-data-utils';
import { useOrthancStore } from './orthanc-store';
import { seriesClassification } from './SeriesClassification';
import { StudySorter } from './StudySorter';

const UNCOMPRESSED_LITTLE_ENDIAN_TRANSFER_SYNTAXES = [
  '1.2.840.10008.1.2',
  '1.2.840.10008.1.2.1',
];
// Enhanced multiframe SOP Class UIDs
export const ENHANCED_MULTIFRAME_SOP_CLASSES = [
  '1.2.840.10008.5.1.4.1.1.4.1', // Enhanced MR Image Storage
  '1.2.840.10008.5.1.4.1.1.2.1', // Enhanced CT Image Storage
];

// type for image indices
export const ImageIndicesSchema = z.object({
  seriesIdx: z.number(),
  sliceIdx: z.number(),
  imageIdx: z.number(),
});
export type ImageIndices = z.infer<typeof ImageIndicesSchema>;

// ToDo
export function validImageIndices(indices: ImageIndices): boolean {
  return (
    indices.seriesIdx >= 0 && indices.sliceIdx >= 0 && indices.imageIdx >= 0
  );
}

export function areImageIndicesEqual(
  indices1: ImageIndices,
  indices2: ImageIndices,
): boolean {
  return (
    indices1.seriesIdx === indices2.seriesIdx
    && indices1.sliceIdx === indices2.sliceIdx
    && indices1.imageIdx === indices2.imageIdx
  );
}

export enum VencDirection2D {
  ThroughPlane = 'ThroughPlane',
  InPlane = 'InPlane',
  Speed = 'Speed',
  Unknown = 'Unknown',
}

// interface for active study
export interface Image {
  uid: string;
}

export interface Slice {
  images: Image[];
}

export interface Series {
  uid: string;
  slices: Slice[];
}

export interface Study {
  series: Series[];
}

export interface StudyInfoDicom {
  PatientID: string;
  PatientName: string;
  PatientBirthDate: string;
  PatientSex: string;
  PatientWeight: number;
  PatientSize: number;
  StudyID: string;
  StudyInstanceUID: string;
  OrthancID: string;
  StudyDate: string;
  StudyTime: string;
  AccessionNumber: string;
  StudyDescription: string;
  Modality: string;
  Manufacturer: string;
  InstitutionName: string;
  MagneticFieldStrength: string;
  ManufacturerModelName: string;
  SoftwareVersions: string;
  ReferringPhysicianName: string;
  LastUpdate: string;
}

export interface HeaderDisplayConfig {
  enabled: boolean;
  default: boolean;
  label: string;
  fallback?: string; // Optional fallback value for display
};

// HeaderDisplayInfo type: maps each StudyInfoDicom property to HeaderDisplayConfig
// This is used to control which properties are displayed in StudyInfoHeader
export type HeaderDisplayInfo = Record<keyof StudyInfoDicom, HeaderDisplayConfig>;

export interface SeriesInfoDicom {
  SeriesInstanceUID: string;
  OrthancID: string;
  SeriesNumber: string;
  SeriesDescription: string;
}

// generic frame interface
export interface ImageInfoDicom {
  SeriesInstanceUID: string;
  SOPInstanceUID: string;
  OrthancInstanceId: string;
  SopClassUID?: string;
  EnhancedObject: boolean;
  Modality: string;
  SeriesDescription?: string;
  PixelPresentation: string;

  InstanceNumber: number;
  AcquisitionNumber: number;
  SpecificCharacterSet?: string;
  TransferSyntaxUID: string;
  ImageType?: string;

  NumberOfFrames?: number;
  Rows: number;
  Columns: number;
  SamplesPerPixel: number;
  BitsAllocated: number;
  BitsStored?: number;
  PixelRepresentation?: number;
  PhotometricInterpretation?: string;
  PlanarConfiguration?: number;

  PixelSpacing: number[]; //  [number, number];
  ImagePositionPatient: number[]; //  [number, number, number];
  ImageOrientationPatient: number[]; // [number, number, number, number, number, number];
  ImageNormalVector: number[]; // [number, number, number];
  CalculatedSliceLocation: number;
  SliceThickness?: number;
  SliceLocation?: string;
  FrameOfReferenceUID?: string;

  ImageComments?: string;
  WindowWidth: number;
  WindowCenter: number;
  RescaleSlope?: number;
  RescaleIntercept?: number;
  RescaleType?: string;

  AcquisitionDate?: string;
  AcquisitionTime?: string;
  AcquisitionDateTime?: string;
  ContentDate?: string;
  ContentTime?: string;
  TemporalPositionIndex?: number;
  TemporalPositionIdentifier?: string;

  ScanOptions?: string;
  SequenceName?: string;
  ProtocolName?: string;
  CalculatedHeartRate?: number;
  NominalInterval?: number;
  HeartRate?: number;

  TriggerTime?: number;
  RepetitionTime?: number;
  InversionTime?: number;
  FlipAngle?: number;
  EchoTime?: number;
  CardiacNumberOfImages?: number;

  // color lut
  // In the case of the Supplemental Palette Color LUT, the stored pixel values less than
  // the second descriptor value are grayscale values. A value greater than the second
  // descriptor value is clamped to the second descriptor value.
  // An input value one greater than the first value mapped is mapped to the second entry in the Lookup Table Data.
  // Subsequent input values are mapped to the subsequent entries in the Lookup Table Data up to an input value equal
  // to number of entries + first value mapped - 1, which is mapped to the last entry in the Lookup Table Data.
  // Input values greater than or equal to number of entries + first value mapped are also mapped to the last entry in the Lookup Table Data.
  // The second value shall be identical for each of the Red, Green, Blue and Alpha Palette Color Lookup Table Descriptors.
  // The third Palette Color Lookup Table Descriptor value specifies the number of bits for each entry in the
  // Lookup Table Data.It shall take the value of 8 or 16. The LUT Data shall be stored in a format equivalent to
  // 8 bits allocated when the number of bits for each entry is 8, and 16 bits allocated when the number of bits for each entry is 16, where in both cases the high bit is equal to bits allocated - 1.
  // The third value shall be identical for each of the Red, Green and Blue Palette Color Lookup Table Descriptors.

  RedPaletteColorLookupTableDescriptor?: number[]; //  [number, number, number]; (0028,1101) US
  GreenPaletteColorLookupTableDescriptor?: number[]; //  [number, number, number]; (0028,1102) US
  BluePaletteColorLookupTableDescriptor?: number[]; //  [number, number, number]; (0028,1103) US
  RedPaletteColorLookupTableData: number[] | undefined; // (0028,1201) OW
  GreenPaletteColorLookupTableData: number[] | undefined; // (0028,1202) OW
  BluePaletteColorLookupTableData: number[] | undefined; // (0028,1203) OW

  // GE_PhaseContrastFlowAxis?: string;
  // GE_VelocityEncoding?: string;
  // GE_VelocityEncodeScale?: string;
  // GE_VasCollapseFlag?: string;
  // GE_VasFlags?: string;
  // Philips_PCVelocity?: string;
  // Philips_OriginalRescaleIntercept?: string;
  // Philips_OriginalRescaleSlope?: string;
  // Philips_OriginalRescaleType?: string;
  // Siemens_VelocityDescription?: string;

  // venc
  IsVencImage2D: boolean;
  Venc2D: number;
  VencDirection2D: VencDirection2D;
  VencScale2D: number;
  IsMagnitudeMasked: boolean;
  VencImageDescription: string;

  seriesIndices?: number[];
  CalculatedSliceTime?: Record<number, number>;
}

export interface FlatStudy {
  StudyInstanceUID: string; // StudyInstanceUID
  OrthancID: string; // Orthanc internal ID
  PatientName: string;
  PatientID: string;
  PatientSex: string;
  PatientBirthDate: string;
  PatientWeight: number;
  PatientSize: number;
  StudyDate: string;
  StudyTime: string;
  AccessionNumber: string;
  StudyDescription: string;
  Modality: string;
  InstitutionName: string;
  ReferringPhysicianName: string;
  Manufacturer: string;
  ManufacturerModelName: string;
  MagneticFieldStrength: string;
  SoftwareVersions: string;
  SeriesCount: number;
  LastUpdate: string;
}

interface State {
  // study info dicom
  studyInfoDicom: StudyInfoDicom;
  // array of seriesInstanceUIDs
  seriesDicom: string[];
  // seriesInstanceUIDs -> series info
  seriesInfoDicom: Record<string, SeriesInfoDicom>;
  // seriesInstanceUIDs -> array of sopInstanceUIDs
  seriesImagesDicom: Record<string, string[]>;
  // sopInstanceUID -> image info
  imageInfoDicom: Record<string, ImageInfoDicom>;
  // series invalidation information
  needsRebuild: Record<string, boolean>;

  manualPatientMeasurements: { PatientWeight: number; PatientSize: number } | null;

  // object of active information to show on AppHeader:
  headerDisplayInfo: HeaderDisplayInfo;

  activeStudy: Study;

  // could be different from dicom series -> index
  seriesThumbnails: Record<number, string>;
  seriesThumbnailDescriptions: Record<number, string>;
  seriesDimensions: Record<number, { sliceCount: number; phaseCount: number }>;

  // vtk images
  vtkImageDataFiFo: VtkImageAndMetaDataCache | null;
  originalVtkImageData: Record<
    string,
    {
      description: string;
      originalData: vtkImageData;
    }
  >;
  overlayVtkImageData: Record<
    string,
    {
      description: string;
      overlayData: vtkImageData;
    }
  >;
  currentViewImageID: Record<string, string>;
  currentViewSeriesID: Record<string, string | null>;
  currentViewMetadata: Record<string, ImageMetadata>;
  currentViewImageIndex: Record<string, number>;
  currentViewSliceIndex: Record<string, number>;
  currentViewSeriesIndex: Record<string, number>;

  // fetching states
  isInitializing: boolean;
  isInitializingThumbnails: boolean;

  // Priority queue (immediate/reactive - user needs this NOW)
  fetchQueue: Record<string, string[]>;
  fetchQueueTimer: number | null;
  fetchTimerInterval: number;

  fetchConcurrencyLimit: number;
  fetchConcurrencyCount: number;

  // Background queue (proactive preloading - low priority)
  backgroundQueue: string[];
  backgroundQueueTimer: number | null;
  backgroundQueueInterval: number;

  backgroundFetchConcurrencyLimit: number;
  backgroundFetchConcurrencyCount: number;

  _inflightCreateVtkImageDataInstances: Record<
    string,
    Promise<vtkImageData | undefined>
  >;
}

export const useCurrentStudyStore = defineStore('current-study-store', {
  state: (): State => ({
    studyInfoDicom: {
      PatientID: '',
      PatientName: '',
      PatientBirthDate: '',
      PatientSex: '',
      StudyID: '',
      StudyInstanceUID: '',
      OrthancID: '',
      StudyDate: '',
      StudyTime: '',
      AccessionNumber: '',
      StudyDescription: '',
      Modality: '',
      Manufacturer: '',
      InstitutionName: '',
      PatientWeight: 0,
      PatientSize: 0,
      MagneticFieldStrength: '',
      ManufacturerModelName: '',
      SoftwareVersions: '',
      ReferringPhysicianName: '',
      LastUpdate: '',
    },
    headerDisplayInfo: {
      PatientID: {
        enabled: false,
        default: false,
        label: 'Patient ID',
        fallback: 'PatientName',
      },
      PatientName: { enabled: true, default: true, label: 'Patient Name' },
      PatientBirthDate: { enabled: true, default: true, label: 'DOB' },
      PatientSex: { enabled: false, default: false, label: 'Sex' },
      StudyID: { enabled: false, default: false, label: 'Study ID' },
      StudyInstanceUID: {
        enabled: false,
        default: false,
        label: 'Study Instance UID',
      },
      OrthancID: {
        enabled: false,
        default: false,
        label: 'Orthanc ID',
      },
      StudyDate: { enabled: false, default: false, label: 'Study Date' },
      StudyTime: { enabled: false, default: false, label: 'Study Time' },
      AccessionNumber: {
        enabled: false,
        default: false,
        label: 'Accession Number',
      },
      StudyDescription: {
        enabled: false,
        default: false,
        label: 'Study Description',
      },
      Modality: { enabled: false, default: false, label: 'Modality' },
      Manufacturer: { enabled: false, default: false, label: 'Manufacturer' },
      InstitutionName: {
        enabled: false,
        default: false,
        label: 'Institution Name',
      },
      PatientWeight: { enabled: false, default: false, label: 'Weight' },
      PatientSize: { enabled: false, default: false, label: 'Size' },
      MagneticFieldStrength: {
        enabled: false,
        default: false,
        label: 'Field Strength',
      },
      ManufacturerModelName: { enabled: false, default: false, label: 'Model' },
      SoftwareVersions: { enabled: false, default: false, label: 'Software' },
      ReferringPhysicianName: {
        enabled: false,
        default: false,
        label: 'Ref. Physician',
      },
      LastUpdate: { enabled: false, default: false, label: 'Import Date' },
    },
    seriesDicom: [],
    seriesInfoDicom: {},
    seriesImagesDicom: {},
    imageInfoDicom: {},
    needsRebuild: {},
    manualPatientMeasurements: null,

    activeStudy: {
      series: [],
    },

    seriesThumbnails: {},
    seriesThumbnailDescriptions: {},
    seriesDimensions: {},

    vtkImageDataFiFo: null as VtkImageAndMetaDataCache | null,
    originalVtkImageData: {},

    overlayVtkImageData: {},

    currentViewImageID: {},
    currentViewSeriesID: {},
    currentViewMetadata: {},
    currentViewImageIndex: {},
    currentViewSliceIndex: {},
    currentViewSeriesIndex: {},

    isInitializing: false,
    isInitializingThumbnails: false,

    // Priority queue
    fetchQueue: markRaw({}),
    fetchQueueTimer: null,
    fetchTimerInterval: 20,
    fetchConcurrencyLimit: 8,
    fetchConcurrencyCount: 0,

    // Background queue
    backgroundQueue: markRaw([]),
    backgroundQueueTimer: null,
    backgroundQueueInterval: 10,
    backgroundFetchConcurrencyLimit: 4,
    backgroundFetchConcurrencyCount: 0,

    _inflightCreateVtkImageDataInstances: {},
  }),
  getters: {
    // retrund undefined if not present
    getStudyInfo: (state) => {
      return state.studyInfoDicom;
    },
    getStudyID: (state) => {
      return state.studyInfoDicom.StudyInstanceUID;
    },
    getActiveHeaderDisplayInfo: (state) => {
      // Return for which enabled = true
      return Object.fromEntries(
        Object.entries(state.headerDisplayInfo).filter(
          ([, info]) => info.enabled,
        ),
      );
    },
    getHeaderDisplayInfo: (state) => {
      // Return all header display info
      return state.headerDisplayInfo;
    },
    getCurrentImagdeMetadataByView: (state) => {
      return (view: string) => state.currentViewMetadata[view];
    },
    getCurrentImageIndexByView: (state) => {
      return (view: string) => state.currentViewImageIndex[view];
    },
    getCurrentSliceIndexByView: (state) => {
      return (view: string) => state.currentViewSliceIndex[view];
    },
    getCurrentSeriesIndexByView: (state) => {
      return (view: string) => state.currentViewSeriesIndex[view];
    },
    getCurrentImageIndicesByView: (state) => {
      return (view: string) => {
        const seriesIndex = state.currentViewSeriesIndex[view];
        const sliceIndex = state.currentViewSliceIndex[view];
        const imageIndex = state.currentViewImageIndex[view];
        return { seriesIndex, sliceIndex, imageIndex };
      };
    },
    getSeriesThumbnails: state => state.seriesThumbnails,
    getSeriesThumbnailDescriptions: state =>
      state.seriesThumbnailDescriptions,
    getSeriesDimensions: state => state.seriesDimensions,

    isInitializingState: state => state.isInitializing,
    isInitializingThumbnailsState: state => state.isInitializingThumbnails,
  },
  actions: {
    // queue lengths
    priorityQueueLength() {
      return Object.values(this.fetchQueue).reduce(
        (sum, q) => sum + q.length,
        0,
      );
    },
    backgroundQueueLength() {
      return this.backgroundQueue.length;
    },
    updateActiveDisplayHeaderInfo(
      newHeaderInfo: Record<keyof StudyInfoDicom, boolean>,
    ) {
      Object.keys(this.headerDisplayInfo).forEach((key) => {
        const typedKey = key as keyof StudyInfoDicom;
        if (newHeaderInfo[typedKey] !== undefined) {
          this.headerDisplayInfo[typedKey].enabled = newHeaderInfo[typedKey];
          // In the future this should be stored as a user preference
        }
      });
    },
    resetActiveDisplayHeaderInfo() {
      Object.keys(this.headerDisplayInfo).forEach((key) => {
        const typedKey = key as keyof StudyInfoDicom;
        this.headerDisplayInfo[typedKey].enabled
          = this.headerDisplayInfo[typedKey].default;
      });
    },
    // called only from the manual entry menu
    setPatientMeasurements(weightKg: number, heightMetres: number) {
      const PatientWeight = Number.isFinite(weightKg) ? weightKg : 0;
      const PatientSize = Number.isFinite(heightMetres) ? heightMetres : 0;
      this.manualPatientMeasurements = { PatientWeight, PatientSize };
      this.studyInfoDicom = { ...this.studyInfoDicom, PatientWeight, PatientSize };
    },
    getPatientBSA(): number | undefined {
      const weightKg = this.studyInfoDicom.PatientWeight;
      const heightCm = this.studyInfoDicom.PatientSize * 100;
      if (
        !Number.isFinite(weightKg)
        || !Number.isFinite(heightCm)
        || weightKg <= 0
        || heightCm <= 0
      ) {
        return undefined;
      }
      return Math.sqrt((heightCm * weightKg) / 3600);
    },
    serializePatientMeasurements() {
      return this.manualPatientMeasurements;
    },
    deserializePatientMeasurements(
      data: null | { PatientWeight: number; PatientSize: number },
    ) {
      if (!data) return;
      this.manualPatientMeasurements = data;
      this.studyInfoDicom = {
        ...this.studyInfoDicom,
        PatientWeight: data.PatientWeight,
        PatientSize: data.PatientSize,
      };
    },
    onDeleteImageData(sopInstanceUID: string) {
      if (this.$proxies.getData(sopInstanceUID))
        this.$proxies.deleteData(sopInstanceUID);

      // // Also clean up the corresponding overlay data proxy
      // const overlayID = `overlay:${sopInstanceUID}`;
      // if (this.$proxies.getData(overlayID)) {
      //   this.$proxies.deleteData(overlayID);
      // }
    },
    // setter
    updateViewImageID(view: string, imageId: string) {
      this.currentViewImageID[view] = imageId;
      this.currentViewSeriesID[view] = this.getSeriesUIDByImageUID(imageId);
      this.currentViewMetadata[view] = this.getVtkMetaData(imageId);
    },
    reset() {
      Object.entries(this.seriesThumbnails).forEach(([, url]) => {
        URL.revokeObjectURL(url);
      });
      this.seriesThumbnailDescriptions = {};
      this.seriesDimensions = {};

      this.fetchQueue = {};
      this.backgroundQueue = [];

      this.fetchConcurrencyCount = 0;
      this.backgroundFetchConcurrencyCount = 0;

      if (this.fetchQueueTimer) {
        window.clearInterval(this.fetchQueueTimer);
        this.fetchQueueTimer = null;
      }

      if (this.backgroundQueueTimer) {
        window.clearInterval(this.backgroundQueueTimer);
        this.backgroundQueueTimer = null;
      }

      // Delete all overlay data proxies
      // Delete all overlay data proxies
      Object.keys(this.overlayVtkImageData).forEach((uid) => {
        const overlayID = `overlay:${uid}`;
        if (this.$proxies.getData(overlayID)) {
          this.$proxies.deleteData(overlayID);
        }
      });

      this.vtkImageDataFiFo?.clear();
      this.vtkImageDataFiFo = null;
      this.originalVtkImageData = {};
      this.overlayVtkImageData = {};

      // todo abort controller
      this._inflightCreateVtkImageDataInstances = {};

      seriesClassification.reset();

      const userHeaderOptions = this.headerDisplayInfo;

      this.$reset();

      // Persist user header options
      this.headerDisplayInfo = userHeaderOptions;
    },
    async initStudy(study: FlatStudy, seriesInfoList: OrthancSeriesInfo[]) {
      if (this.isInitializing) return;

      if (!study.StudyInstanceUID || !study.OrthancID) return;

      this.reset();
      const { resetAppState } = await import('../resetAppState');
      resetAppState();

      this.isInitializing = true;

      this.vtkImageDataFiFo = new VtkImageAndMetaDataCache(
        this.onDeleteImageData.bind(this),
        2000,
      );

      const orthancStore = useOrthancStore();

      // Build a SeriesInstanceUID → SeriesNumber lookup
      const seriesNumberMap: Record<string, string> = {};
      seriesInfoList.forEach((s) => {
        seriesNumberMap[s.MainDicomTags.SeriesInstanceUID]
          = s.MainDicomTags.SeriesNumber || '';
      });
      this.studyInfoDicom = {
        ...this.studyInfoDicom,
        ...study,
        PatientWeight: Number(study.PatientWeight) || 0,
        PatientSize: Number(study.PatientSize) || 0,
      };

      try {
        const ocg19 = await orthancStore.fetchOcg19Metadata(study.OrthancID);

        seriesInfoList.forEach((seriesInfo) => {
          this.importSeries(seriesInfo);
        });
        ocg19.forEach((inst) => {
          this.importInstance(inst);
        });
        this.populateAndSortActiveStudy();
        void this.initSeriesThumbnails();
        seriesClassification.init();
      }
      catch (error) {
        console.error('Error loading study metadata:', error);
        this.reset();
      }
      finally {
        this.isInitializing = false;
      }
    },
    importSeries(seriesInfo: OrthancSeriesInfo) {
      const seriesKey = seriesInfo.MainDicomTags.SeriesInstanceUID;
      if (seriesKey && !(seriesKey in this.seriesInfoDicom)) {
        this.seriesInfoDicom[seriesKey] = {
          SeriesInstanceUID: seriesKey,
          OrthancID: seriesInfo.ID,
          SeriesNumber: seriesInfo.MainDicomTags.SeriesNumber || '',
          SeriesDescription: seriesInfo.MainDicomTags.SeriesDescription || '',
        };
        this.seriesImagesDicom[seriesKey] = [];
        this.seriesDicom.push(seriesKey);
      }
    },
    importInstance(instanceInfo: Ocg19InstanceInfo) {
      // utils
      function calculateImageNormalVector(iop: number[]) {
        const normal = [0, 0, 0];
        normal[0] = iop[1] * iop[5] - iop[2] * iop[4];
        normal[1] = iop[2] * iop[3] - iop[0] * iop[5];
        normal[2] = iop[0] * iop[4] - iop[1] * iop[3];
        return normal;
      }

      function calculateSliceLocation(ipp: number[], nrm: number[]) {
        // dot product of imageNormalVector and imagePositionPatient
        const sliceLocation
          = nrm[0] * ipp[0] + nrm[1] * ipp[1] + nrm[2] * ipp[2];
        return sliceLocation;
      }

      const instance: ImageInfoDicom = {
        // common fields, might get modifications for per frame values
        SeriesInstanceUID: instanceInfo.SeriesInstanceUID,
        SOPInstanceUID: instanceInfo.SOPInstanceUID,
        OrthancInstanceId: instanceInfo.OrthancInstanceId,
        SopClassUID: instanceInfo.SOPClassUID,
        Modality: instanceInfo.Modality,
        SeriesDescription: instanceInfo.SeriesDescription,
        SpecificCharacterSet: instanceInfo.SpecificCharacterSet,
        TransferSyntaxUID: instanceInfo.TransferSyntaxUID,
        NumberOfFrames: Number.parseInt(instanceInfo.NumberOfFrames || '1', 10),
        PhotometricInterpretation:
          instanceInfo.PhotometricInterpretation || 'MONOCHROME2',
        PlanarConfiguration: Number.parseInt(
          instanceInfo.PlanarConfiguration || '0',
          10,
        ),
        Rows: Number.parseInt(instanceInfo.Rows || '1', 10),
        Columns: Number.parseInt(instanceInfo.Columns || '1', 10),
        SamplesPerPixel: Number.parseInt(
          instanceInfo.SamplesPerPixel || '1',
          10,
        ),
        BitsAllocated: Number.parseInt(instanceInfo.BitsAllocated || '1', 10),
        BitsStored: Number.parseInt(instanceInfo.BitsStored || '1', 10),
        PixelRepresentation: Number.parseInt(
          instanceInfo.PixelRepresentation || '0',
          10,
        ),
        FrameOfReferenceUID: instanceInfo.FrameOfReferenceUID,
        ImageComments: instanceInfo.ImageComments || '',
        ScanOptions: instanceInfo.ScanOptions || '',
        SequenceName: instanceInfo.SequenceName || '',
        ProtocolName: instanceInfo.ProtocolName || '',
        RepetitionTime: Number.parseFloat(instanceInfo.RepetitionTime || '0'),
        InversionTime: Number.parseFloat(instanceInfo.InversionTime || '0'),
        FlipAngle: Number.parseFloat(instanceInfo.FlipAngle || '0'),
        InstanceNumber: Number.parseInt(instanceInfo.InstanceNumber || '1', 10),
        CardiacNumberOfImages: Number.parseInt(
          instanceInfo.CardiacNumberOfImages || '0',
          10,
        ),
        HeartRate: Number.parseFloat(instanceInfo.HeartRate || '0'),

        // defaults
        EnhancedObject: false,
        PixelPresentation: '',
        AcquisitionNumber: 0,
        ImageType: instanceInfo.ImageType || '',
        PixelSpacing: [0, 0],
        ImagePositionPatient: [0, 0, 0],
        ImageOrientationPatient: [1, 0, 0, 0, 1, 0],
        ImageNormalVector: [0, 0, 1],
        CalculatedSliceLocation: 0,
        WindowCenter: 0,
        WindowWidth: 0,
        RescaleSlope: 1,
        RescaleIntercept: 0,
        RescaleType: '',
        EchoTime: 0,
        TriggerTime: 0,
        RedPaletteColorLookupTableDescriptor: undefined,
        GreenPaletteColorLookupTableDescriptor: undefined,
        BluePaletteColorLookupTableDescriptor: undefined,
        RedPaletteColorLookupTableData: undefined,
        GreenPaletteColorLookupTableData: undefined,
        BluePaletteColorLookupTableData: undefined,
        IsVencImage2D: false,
        Venc2D: 0,
        VencDirection2D: VencDirection2D.Unknown,
        VencScale2D: 0,
        IsMagnitudeMasked: false,
        VencImageDescription: '',
      };

      const isEnhancedObject = ENHANCED_MULTIFRAME_SOP_CLASSES.includes(
        instanceInfo.SOPClassUID,
      );

      if (isEnhancedObject) {
        for (let i = 0; i < (instance.NumberOfFrames ?? 1); i++) {
          const rawPS = instanceInfo.PerFrame[i].PixelSpacing
            ?? instanceInfo.PixelSpacing ?? [0, 0];
          const pixelSpacing = (
            Array.isArray(rawPS) ? rawPS : String(rawPS).split('\\')
          ).map(Number);

          const rawIPP = instanceInfo.PerFrame[i].ImagePositionPatient
            ?? instanceInfo.ImagePositionPatient ?? [0, 0, 0];
          const imagePositionPatient = (
            Array.isArray(rawIPP) ? rawIPP : String(rawIPP).split('\\')
          ).map(Number);

          const rawIOP = instanceInfo.PerFrame[i].ImageOrientationPatient
            ?? instanceInfo.ImageOrientationPatient ?? [1, 0, 0, 0, 1, 0];
          const imageOrientationPatient = (
            Array.isArray(rawIOP) ? rawIOP : String(rawIOP).split('\\')
          ).map(Number);

          const imageNormalVector = calculateImageNormalVector(
            imageOrientationPatient,
          );

          const sliceLocation = calculateSliceLocation(
            imagePositionPatient,
            imageNormalVector,
          );

          const frameInstance = { ...instance };
          frameInstance.EnhancedObject = true;
          frameInstance.InstanceNumber = i + 1;
          frameInstance.SOPInstanceUID = `${instance.SOPInstanceUID}-${i + 1}`;
          const rawAcquisitionNumber
            = instanceInfo.PerFrame[i].FrameAcquisitionNumber
              ?? instanceInfo.AcquisitionNumber
              ?? 0;
          frameInstance.AcquisitionNumber = Number.parseInt(
            rawAcquisitionNumber || '0',
            10,
          );
          frameInstance.ImageType
            = instanceInfo.PerFrame[i].FrameType ?? instanceInfo.ImageType ?? '';
          frameInstance.PixelPresentation
            = instanceInfo.PerFrame[i].PixelPresentation
              ?? instanceInfo.PixelPresentation
              ?? 'MONOCHROME';
          frameInstance.PixelSpacing = pixelSpacing;
          frameInstance.ImagePositionPatient = imagePositionPatient;
          frameInstance.ImageOrientationPatient = imageOrientationPatient;
          frameInstance.ImageNormalVector = imageNormalVector;
          frameInstance.CalculatedSliceLocation = sliceLocation;
          const rawSliceThickness
            = instanceInfo.PerFrame[i].SliceThickness
              ?? instanceInfo.SliceThickness
              ?? 0;
          frameInstance.SliceThickness = Number.parseFloat(
            rawSliceThickness || '0',
          );
          frameInstance.SliceLocation = instanceInfo.SliceLocation ?? '';
          const rawWindowWidth
            = instanceInfo.PerFrame[i].WindowWidth
              ?? instanceInfo.WindowWidth
              ?? 0;
          frameInstance.WindowWidth = Number.parseInt(
            rawWindowWidth || '0',
            10,
          );
          const rawWindowCenter
            = instanceInfo.PerFrame[i].WindowCenter
              ?? instanceInfo.WindowCenter
              ?? 0;
          frameInstance.WindowCenter = Number.parseInt(
            rawWindowCenter || '0',
            10,
          );
          const rawRescaleSlope
            = instanceInfo.PerFrame[i].RescaleSlope
              ?? instanceInfo.RescaleSlope
              ?? 1;
          frameInstance.RescaleSlope = Number.parseFloat(
            rawRescaleSlope || '1',
          );
          const rawRescaleIntercept
            = instanceInfo.PerFrame[i].RescaleIntercept
              ?? instanceInfo.RescaleIntercept
              ?? 0;
          frameInstance.RescaleIntercept = Number.parseFloat(
            rawRescaleIntercept || '0',
          );
          frameInstance.RescaleType
            = instanceInfo.PerFrame[i].RescaleType
              ?? instanceInfo.RescaleType
              ?? '';
          frameInstance.PixelPresentation
            = instanceInfo.PerFrame[i].PixelPresentation
              ?? instanceInfo.PixelPresentation
              ?? 'MONOCHROME2';
          const rawTemporalPositionIndex
            = instanceInfo.PerFrame[i].TemporalPositionIndex ?? 0;
          frameInstance.TemporalPositionIndex = Number.parseInt(
            rawTemporalPositionIndex || '0',
            10,
          );
          frameInstance.AcquisitionDateTime
            = instanceInfo.PerFrame[i].FrameReferenceDateTime
              ?? instanceInfo.AcquisitionDateTime
              ?? '';
          const rawTriggerTime
            = instanceInfo.PerFrame[i].NominalCardiacTriggerDelayTime
              ?? instanceInfo.TriggerTime
              ?? '0';
          frameInstance.TriggerTime = Number.parseFloat(rawTriggerTime || '0');
          const rawEchoTime
            = instanceInfo.PerFrame[i].EffectiveEchoTime
              ?? instanceInfo.EchoTime
              ?? '0';
          frameInstance.EchoTime = Number.parseFloat(rawEchoTime || '0');
          const rawNominalInterval
            = instanceInfo.PerFrame[i].RRIntervalTimeNominal
              ?? instanceInfo.NominalInterval
              ?? 0;
          frameInstance.NominalInterval = Number.parseFloat(
            rawNominalInterval || '0',
          );

          if (frameInstance.NominalInterval > 0) {
            frameInstance.CalculatedHeartRate
              = 60000 / frameInstance.NominalInterval;
          }
          else {
            frameInstance.CalculatedHeartRate = 0;
          }

          const imageKey = frameInstance.SOPInstanceUID;
          if (imageKey && !(imageKey in this.imageInfoDicom)) {
            this.imageInfoDicom[imageKey] = frameInstance;
            this.seriesImagesDicom[instanceInfo.SeriesInstanceUID].push(
              imageKey,
            );
          }
        }
        return;
      }

      const rawPS = instanceInfo.PixelSpacing ?? [0, 0];
      const pixelSpacing = (
        Array.isArray(rawPS) ? rawPS : String(rawPS).split('\\')
      ).map(Number);

      const rawIPP = instanceInfo.ImagePositionPatient ?? [0, 0, 0];
      const imagePositionPatient = (
        Array.isArray(rawIPP) ? rawIPP : String(rawIPP).split('\\')
      ).map(Number);

      const rawIOP = instanceInfo.ImageOrientationPatient ?? [1, 0, 0, 0, 1, 0];
      const imageOrientationPatient = (
        Array.isArray(rawIOP) ? rawIOP : String(rawIOP).split('\\')
      ).map(Number);

      const imageNormalVector = calculateImageNormalVector(
        imageOrientationPatient,
      );

      const sliceLocation = calculateSliceLocation(
        imagePositionPatient,
        imageNormalVector,
      );

      const rawAcquisitionNumber = instanceInfo.AcquisitionNumber ?? 0;
      instance.AcquisitionNumber = Number.parseInt(
        rawAcquisitionNumber || '0',
        10,
      );
      instance.ImageType = instanceInfo.ImageType ?? '';
      instance.PixelPresentation
        = instanceInfo.PixelPresentation ?? 'MONOCHROME2';
      instance.PixelSpacing = pixelSpacing;
      instance.ImagePositionPatient = imagePositionPatient;
      instance.ImageOrientationPatient = imageOrientationPatient;
      instance.ImageNormalVector = imageNormalVector;
      instance.CalculatedSliceLocation = sliceLocation;
      const rawSliceThickness = instanceInfo.SliceThickness ?? 0;
      instance.SliceThickness = Number.parseFloat(rawSliceThickness || '0');
      instance.SliceLocation = instanceInfo.SliceLocation ?? '';
      const rawWindowWidth = instanceInfo.WindowWidth ?? 0;
      instance.WindowWidth = Number.parseInt(rawWindowWidth || '0', 10);
      const rawWindowCenter = instanceInfo.WindowCenter ?? 0;
      instance.WindowCenter = Number.parseInt(rawWindowCenter || '0', 10);
      const rawRescaleSlope = instanceInfo.RescaleSlope ?? 1;
      instance.RescaleSlope = Number.parseFloat(rawRescaleSlope || '1');
      const rawRescaleIntercept = instanceInfo.RescaleIntercept ?? 0;
      instance.RescaleIntercept = Number.parseFloat(rawRescaleIntercept || '0');
      instance.RescaleType = instanceInfo.RescaleType ?? '';
      instance.PixelPresentation
        = instanceInfo.PixelPresentation ?? 'MONOCHROME';
      instance.AcquisitionDate = instanceInfo.AcquisitionDate ?? '';
      instance.AcquisitionTime = instanceInfo.AcquisitionTime ?? '';
      instance.AcquisitionDateTime = instanceInfo.AcquisitionDateTime ?? '';
      instance.ContentDate = instanceInfo.ContentDate ?? '';
      instance.ContentTime = instanceInfo.ContentTime ?? '';
      const rawTriggerTime = instanceInfo.TriggerTime ?? '0';
      instance.TriggerTime = Number.parseFloat(rawTriggerTime || '0');
      const rawEchoTime = instanceInfo.EchoTime ?? '0';
      instance.EchoTime = Number.parseFloat(rawEchoTime || '0');
      const rawNominalInterval = instanceInfo.NominalInterval ?? 0;
      instance.NominalInterval = Number.parseFloat(rawNominalInterval || '0');
      const rawHeartRate = instanceInfo.HeartRate ?? 0;
      instance.HeartRate = Number.parseFloat(rawHeartRate || '0');

      if (instance.HeartRate > 0) {
        instance.CalculatedHeartRate = instance.HeartRate;
      }
      else if (instance.NominalInterval > 0) {
        instance.CalculatedHeartRate = 60000 / instance.NominalInterval;
      }
      else {
        instance.CalculatedHeartRate = 0;
      }

      const rawRedDesc = instanceInfo.RedPaletteColorLookupTableDescriptor;
      instance.RedPaletteColorLookupTableDescriptor = rawRedDesc
        ? (Array.isArray(rawRedDesc)
            ? rawRedDesc
            : String(rawRedDesc).split('\\')
          ).map(Number)
        : undefined;

      const rawGreenDesc = instanceInfo.GreenPaletteColorLookupTableDescriptor;
      instance.GreenPaletteColorLookupTableDescriptor = rawGreenDesc
        ? (Array.isArray(rawGreenDesc)
            ? rawGreenDesc
            : String(rawGreenDesc).split('\\')
          ).map(Number)
        : undefined;

      const rawBlueDesc = instanceInfo.BluePaletteColorLookupTableDescriptor;
      instance.BluePaletteColorLookupTableDescriptor = rawBlueDesc
        ? (Array.isArray(rawBlueDesc)
            ? rawBlueDesc
            : String(rawBlueDesc).split('\\')
          ).map(Number)
        : undefined;

      instance.RedPaletteColorLookupTableData = undefined;
      instance.GreenPaletteColorLookupTableData = undefined;
      instance.BluePaletteColorLookupTableData = undefined;

      const imageKey = instance.SOPInstanceUID;
      if (imageKey && !(imageKey in this.imageInfoDicom)) {
        this.imageInfoDicom[imageKey] = instance;
        this.seriesImagesDicom[instance.SeriesInstanceUID].push(imageKey);
      }

      // init venc per vendor
      if (this.studyInfoDicom.Manufacturer.toLowerCase().includes('siemens')) {
        const imageType = instance.ImageType;
        const sequenceName = instance.SequenceName;
        if (
          imageType.includes('ORIGINAL')
          && imageType.includes('PRIMARY')
          && imageType.includes('P')
          && (sequenceName?.startsWith('fl2d')
            || sequenceName?.startsWith('pc2d')
            || sequenceName?.startsWith('*fl2d')
            || sequenceName?.startsWith('*pc2d'))
        ) {
          const match = sequenceName?.match(/_v(\d+)([a-z]+)?/i);
          const venc = match ? Number.parseInt(match[1], 10) : null;
          const direction = match?.[2] || '';

          // ToDo: error handling
          if (venc && instance.RescaleIntercept) {
            instance.IsVencImage2D = true;
            instance.Venc2D = venc;
            instance.VencScale2D = venc / Math.abs(instance.RescaleIntercept);

            if (direction === 'in' || direction === 'out') {
              instance.VencDirection2D = VencDirection2D.ThroughPlane;
            }
            else {
              instance.VencDirection2D = VencDirection2D.InPlane;
            }
            instance.VencImageDescription = `VENC: ${venc}cm/s ${instance.VencDirection2D}`;
          }
        }
      }
      else if (
        this.studyInfoDicom.Manufacturer.toLowerCase().includes('philips')
      ) {
        // TODO: implement venc 2d for philips
      }
      else if (
        this.studyInfoDicom.Manufacturer.toLowerCase().includes('ge')
      ) {
        if (instance.ScanOptions?.split('\\').includes('VASCPC_GEMS')) {
          instance.Venc2D
            = Number.parseFloat(instanceInfo.Private?.VelocityEncoding ?? '0')
              / 10;
          instance.VencDirection2D = VencDirection2D.Unknown;
          instance.IsMagnitudeMasked
            = instanceInfo.Private?.IsMagnitudeMasked ?? undefined;

          // vasCollapseFlag:
          // 1 - Collapse Image not used in flow analysis
          // 2 - Magnitude
          // 3 - Sagittal plane RL
          // 4 - Coronal plane AP
          // 5 - Axial plane SI
          // 7 - ALL directions, SPEED
          // 8 - Magnitude oblique plane
          // 9 - Sagittal oblique plane RL
          // 10 - Coronal oblique plane AP
          // 11 - Axial oblique plane SI

          const vasCollapseFlag = Number.parseInt(
            instanceInfo.Private?.VasCollapseFlag ?? '0',
            10,
          );
          const vencScaleGE = Number.parseFloat(
            instanceInfo.Private?.VelocityEncodeScale ?? '0',
          );
          instance.VencScale2D = instance.Venc2D / (vencScaleGE * Math.PI);

          if (
            instance.IsMagnitudeMasked !== undefined
            && instance.Venc2D > 0
            && instance.VencScale2D !== 0
            && ![0, 1, 2, 8].includes(vasCollapseFlag)
          ) {
            instance.IsVencImage2D = true;
            instance.ImageType = `${instance.ImageType}_VENC`;
            const vencString = `VENC: ${instance.Venc2D}cm/s`;

            switch (vasCollapseFlag) {
              case 3:
                instance.VencDirection2D = VencDirection2D.ThroughPlane;
                instance.VencImageDescription = `${vencString} (RL)`;
                break;
              case 4:
                instance.VencDirection2D = VencDirection2D.ThroughPlane;
                instance.VencImageDescription = `${vencString} (AP)`;
                break;
              case 5:
                instance.VencDirection2D = VencDirection2D.ThroughPlane;
                instance.VencImageDescription = `${vencString} (SI)`;
                break;
              case 7:
                instance.VencDirection2D = VencDirection2D.Speed;
                instance.VencImageDescription = `${vencString} (Speed)`;
                break;
              case 9:
                instance.VencDirection2D = VencDirection2D.ThroughPlane;
                instance.VencImageDescription = `${vencString} (RL Oblique)`;
                break;
              case 10:
                instance.VencDirection2D = VencDirection2D.ThroughPlane;
                instance.VencImageDescription = `${vencString} (AP Oblique)`;
                break;
              case 11:
                instance.VencDirection2D = VencDirection2D.ThroughPlane;
                instance.VencImageDescription = `${vencString} (SI Oblique)`;
                break;
              default:
                instance.VencDirection2D = VencDirection2D.Unknown;
                instance.VencImageDescription = `${vencString} (Unknown)`;
                break;
            }

            if (instance.IsMagnitudeMasked) {
              instance.VencImageDescription = `${vencString} (Mag Mask)`;
            }
          }
        }
      }
    },
    async initSeriesThumbnails() {
      if (this.isInitializingThumbnails) return;
      this.isInitializingThumbnails = true;

      const firstInstanceUIDs = this.activeStudy.series.map(
        (series: Series) => series.slices[0].images[0].uid,
      );

      const seriesDescriptions: Record<number, string> = {};
      this.activeStudy.series.forEach((series: Series, index: number) => {
        seriesDescriptions[index] = (
          this.seriesInfoDicom[series.uid]?.SeriesDescription ?? ''
        ).trim();
      });
      this.seriesThumbnailDescriptions = seriesDescriptions;

      const dimensions: Record<
        number,
        { sliceCount: number; phaseCount: number }
      > = {};
      this.activeStudy.series.forEach((series: Series, index: number) => {
        dimensions[index] = {
          sliceCount: series.slices.length,
          phaseCount: series.slices[0].images.length,
        };
      });
      this.seriesDimensions = dimensions;

      try {
        await Promise.all(
          firstInstanceUIDs.map(async (uid, index) =>
            this.downloadThumbnail(index, uid),
          ),
        );
      }
      catch (error) {
        console.error('Error initSeriesThumbnails:', error);
      }
      finally {
        this.isInitializingThumbnails = false;
      }
    },
    async downloadThumbnail(index: number, sopInstanceUID: string) {
      const orthancStore = useOrthancStore();
      const realInstanceUID = sopInstanceUID.split('-')[0];
      const orthancInstanceId = orthancStore.resolveInstanceId(realInstanceUID);

      if (!orthancInstanceId) return;
      const thumb = await orthancStore.fetchThumbnail(orthancInstanceId);
      if (!thumb) return;
      this.seriesThumbnails[index] = thumb;
    },
    hasPaletteColorLookupTable(sopInstanceUID: string) {
      const info = this.imageInfoDicom[sopInstanceUID];
      if (!info) return false;
      return (
        info.RedPaletteColorLookupTableDescriptor !== undefined
        && info.GreenPaletteColorLookupTableDescriptor !== undefined
        && info.BluePaletteColorLookupTableDescriptor !== undefined
      );
    },
    async fetchPaletteColorLookupTable(sopInstanceUID: string) {
      if (!this.hasPaletteColorLookupTable(sopInstanceUID)) {
        return;
      }
      const info = this.imageInfoDicom[sopInstanceUID];
      if (
        info.RedPaletteColorLookupTableData
        && info.GreenPaletteColorLookupTableData
        && info.BluePaletteColorLookupTableData
      ) {
        return;
      }

      const orthancStore = useOrthancStore();
      const lut = await orthancStore.fetchPaletteLut(info.OrthancInstanceId);
      if (lut.red && lut.green && lut.blue) {
        info.RedPaletteColorLookupTableData = Array.from(lut.red);
        info.GreenPaletteColorLookupTableData = Array.from(lut.green);
        info.BluePaletteColorLookupTableData = Array.from(lut.blue);
      }
    },

    async getInstanceMetadata(sopInstanceUID: string) {
      const orthancStore = useOrthancStore();
      const realInstanceUID = sopInstanceUID.split('-')[0];
      const dicomDump
        = await orthancStore.getInstanceSimplifiedTags(realInstanceUID);

      return dicomDump;
    },
    getSerieCount() {
      return this.activeStudy.series.length;
    },
    getSliceCount(seriesIndex: number) {
      if (
        seriesIndex < 0
        || !this.activeStudy.series.length
        || seriesIndex >= this.activeStudy.series.length
      ) {
        return 0;
      }
      return this.activeStudy.series[seriesIndex].slices.length;
    },
    getMaxPerSliceImageCount(seriesIndex: number) {
      if (
        seriesIndex < 0
        || !this.activeStudy.series.length
        || seriesIndex >= this.activeStudy.series.length
      ) {
        return 0;
      }
      const count = this.activeStudy.series[seriesIndex].slices.reduce(
        (max, slice) => Math.max(max, slice.images.length),
        0,
      );
      if (!count) return 0;
      return count;
    },
    getImagesCount(seriesIndex: number, sliceIndex: number) {
      if (
        seriesIndex < 0
        || sliceIndex < 0
        || !this.activeStudy.series.length
        || seriesIndex >= this.activeStudy.series.length
        || sliceIndex >= this.activeStudy.series[seriesIndex].slices.length
      ) {
        return 0;
      }
      return this.activeStudy.series[seriesIndex].slices[sliceIndex].images
        .length;
    },
    getSliceImageIDs(seriesIndex: number, sliceIndex: number): string[] {
      if (
        !this.activeStudy.series.length
        || seriesIndex >= this.activeStudy.series.length
        || sliceIndex >= this.activeStudy.series[seriesIndex].slices.length
      ) {
        return [];
      }
      return this.activeStudy.series[seriesIndex].slices[sliceIndex].images.map(
        image => image.uid,
      );
    },
    getPhaseImageUIDs(seriesIndex: number, phaseIndex: number) {
      if (
        !this.activeStudy.series.length
        || seriesIndex >= this.activeStudy.series.length
      ) {
        return [];
      }
      // Collect all image UIDs from the specified phase (column) across all slices
      const imageUIDs: string[] = [];

      // Iterate through all slices in the series
      for (
        let sliceIdx = 0;
        sliceIdx < this.activeStudy.series[seriesIndex].slices.length;
        sliceIdx++
      ) {
        const slice = this.activeStudy.series[seriesIndex].slices[sliceIdx];
        // Check if this slice has an image at the specified phase index
        if (phaseIndex < slice.images.length) {
          imageUIDs.push(slice.images[phaseIndex].uid);
        }
      }

      return imageUIDs;
    },
    getSeriesImageUIDs(seriesIndex: number) {
      if (
        !this.activeStudy.series.length
        || seriesIndex >= this.activeStudy.series.length
        || seriesIndex < 0
      ) {
        return [];
      }
      return this.activeStudy.series[seriesIndex].slices.flatMap(slice =>
        slice.images.map(image => image.uid),
      );
    },
    // study index navigation
    getNextImageIndex(indices: ImageIndices) {
      // if (!this.activeStudy.series.length) return null;
      const slice
        = this.activeStudy.series[indices.seriesIdx].slices[indices.sliceIdx];
      let nextImageIndex = indices.imageIdx + 1;
      if (nextImageIndex >= slice.images.length) nextImageIndex = 0;
      return {
        seriesIdx: indices.seriesIdx,
        sliceIdx: indices.sliceIdx,
        imageIdx: nextImageIndex,
      };
    },
    getPreviousImageIndex(indices: ImageIndices) {
      // if (!this.activeStudy.series.length) return null;
      const slice
        = this.activeStudy.series[indices.seriesIdx].slices[indices.sliceIdx];
      let previousImageIndex = indices.imageIdx - 1;
      if (previousImageIndex < 0) previousImageIndex = slice.images.length - 1;
      return {
        seriesIdx: indices.seriesIdx,
        sliceIdx: indices.sliceIdx,
        imageIdx: previousImageIndex,
      };
    },
    getNextSliceIndex(indices: ImageIndices) {
      // if (!this.activeStudy.series.length) return null;
      let nextSliceIndex = indices.sliceIdx + 1;
      if (
        nextSliceIndex
        >= this.activeStudy.series[indices.seriesIdx].slices.length
      ) {
        nextSliceIndex = 0;
      }
      const slice
        = this.activeStudy.series[indices.seriesIdx].slices[nextSliceIndex];
      let correctedImageIndex = indices.imageIdx;
      if (indices.imageIdx >= slice.images.length)
        correctedImageIndex = slice.images.length - 1;
      return {
        seriesIdx: indices.seriesIdx,
        sliceIdx: nextSliceIndex,
        imageIdx: correctedImageIndex,
      };
    },
    getPreviousSliceIndex(indices: ImageIndices) {
      // if (!this.activeStudy.series.length) return null;
      let previousSliceIndex = indices.sliceIdx - 1;
      if (previousSliceIndex < 0) {
        previousSliceIndex
          = this.activeStudy.series[indices.seriesIdx].slices.length - 1;
      }
      const slice
        = this.activeStudy.series[indices.seriesIdx].slices[previousSliceIndex];
      let correctedImageIndex = indices.imageIdx;
      if (indices.imageIdx >= slice.images.length)
        correctedImageIndex = slice.images.length - 1;
      return {
        seriesIdx: indices.seriesIdx,
        sliceIdx: previousSliceIndex,
        imageIdx: correctedImageIndex,
      };
    },
    getNextSeriesIndex(seriesIndex: number) {
      // if (!this.activeStudy.series.length) return null;
      let nextSeriesIndex = seriesIndex + 1;
      if (nextSeriesIndex >= this.activeStudy.series.length)
        nextSeriesIndex = 0;
      return {
        seriesIdx: nextSeriesIndex,
        sliceIdx: 0,
        imageIdx: 0,
      };
    },
    getPreviousSeriesIndex(seriesIndex: number) {
      // if (!this.activeStudy.series.length) return null;
      let previousSeriesIndex = seriesIndex - 1;
      if (previousSeriesIndex < 0)
        previousSeriesIndex = this.activeStudy.series.length - 1;
      return {
        seriesIdx: previousSeriesIndex,
        sliceIdx: 0,
        imageIdx: 0,
      };
    },

    getImageUID(indices: ImageIndices) {
      if (!this.activeStudy.series.length) return '';
      const uid = this.getImageUIDs(indices).sopInstanceUID;
      return uid;
    },
    getSeriesUID(seriesIndex: number) {
      if (!this.activeStudy.series.length) return '';
      const { uid } = this.activeStudy.series[seriesIndex];
      // Series UID sometimes ends with # number, remove if present:
      const seriesUID = uid.split('#')[0];
      return seriesUID;
    },
    getSeriesUIDByImageUID(sopInstanceUID: string) {
      if (
        !this.activeStudy.series.length
        || !this.imageInfoDicom[sopInstanceUID]
      ) {
        return null;
      }
      const uid = this.imageInfoDicom[sopInstanceUID].SeriesInstanceUID;
      return uid;
    },
    getSliceImageUIDsbyView(view: string) {
      try {
        // Validate the view input
        if (!view || typeof view !== 'string') {
          throw new Error(
            'Invalid view parameter. It must be a non-empty string.',
          );
        }

        const viewportStateStore = useViewportStateStore();
        const imageIndices = viewportStateStore.getSelectedImageIndices(view);
        const seriesIndex = imageIndices.seriesIdx;
        const sliceIndex = imageIndices.sliceIdx;

        // Validate seriesIndex and sliceIndex
        if (seriesIndex === undefined || sliceIndex === undefined) {
          throw new Error(
            `Unable to retrieve seriesIndex or sliceIndex for the provided view: ${view}`,
          );
        }

        const imageUIDsfromStore = this.getSliceImageUIDs(
          seriesIndex,
          sliceIndex,
        );

        // Validate imageUIDsfromStore
        if (
          !Array.isArray(imageUIDsfromStore)
          || imageUIDsfromStore.length === 0
        ) {
          console.warn(
            `No image UIDs found for seriesIndex: ${seriesIndex} and sliceIndex: ${sliceIndex}`,
          );
          return [];
        }

        return imageUIDsfromStore;
      }
      catch (error) {
        console.error('Error in getSliceImageUIDsbyView:', error);
        return []; // Return an empty array or handle the error as needed
      }
    },
    getIndexByImageUID(sopInstanceUID: string) {
      if (!this.activeStudy || !this.activeStudy.series.length) {
        return null;
      }

      if (!this.imageInfoDicom[sopInstanceUID]) {
        console.error(
          `Image with UID ${sopInstanceUID} not found in imageInfoDicom`,
        );
        return null;
      }

      const seriesUID = this.imageInfoDicom[sopInstanceUID].SeriesInstanceUID;
      const studySeries = this.activeStudy.series;
      const seriesIndex = studySeries.findIndex(
        series => series.uid === seriesUID,
      );
      if (seriesIndex === -1) {
        console.error(`Series with UID ${seriesUID} not found in activeStudy`);
        return null;
      }

      const { slices } = this.activeStudy.series[seriesIndex];
      let sliceIndex = -1;
      let imageIndex = -1;

      for (let i = 0; i < slices.length; i++) {
        const index = slices[i].images.findIndex(
          image => image.uid === sopInstanceUID,
        );
        if (index !== -1) {
          sliceIndex = i;
          imageIndex = index;
          break;
        }
      }

      if (sliceIndex === -1 || imageIndex === -1) {
        console.error(
          `Image with UID ${sopInstanceUID} not found in activeStudy`,
        );
        return null;
      }

      const imageInfo = {
        imageUID: sopInstanceUID,
        imageSeriesUID: seriesUID,
        imageSeriesIndex: seriesIndex,
        imageSliceIndex: sliceIndex,
        imageIndex,
      };

      return imageInfo;
    },
    getSliceImageUIDs(seriesIndex: number, sliceIndex: number) {
      if (!this.activeStudy.series.length) return null;
      const slice = this.activeStudy.series[seriesIndex]?.slices[sliceIndex];
      if (!slice) {
        console.warn(
          `Slice not found for seriesIndex: ${seriesIndex}, sliceIndex: ${sliceIndex}`,
          this.activeStudy,
        );
        return null;
      }
      return slice.images.map(image => image.uid);
    },
    getImageUIDs(indices: ImageIndices) {
      const defaultIDs = {
        studyInstanceUID: '',
        seriesInstanceUID: '',
        sopInstanceUID: '',
      };

      // Validate all indices are non-negative
      if (
        indices.seriesIdx < 0
        || indices.sliceIdx < 0
        || indices.imageIdx < 0
      ) {
        return defaultIDs;
      }

      if (!this.activeStudy.series.length) {
        console.warn('No series to get image from in activeStudy.');
        return defaultIDs;
      }

      // Validate series index is in bounds
      if (indices.seriesIdx >= this.activeStudy.series.length) {
        return defaultIDs;
      }

      const series = this.activeStudy.series[indices.seriesIdx];

      // Validate slice index is in bounds
      if (indices.sliceIdx >= series.slices.length) {
        return defaultIDs;
      }

      const slice = series.slices[indices.sliceIdx];

      // Validate image index is in bounds
      if (indices.imageIdx >= slice.images.length) {
        return defaultIDs;
      }

      const sopInstanceUID = slice.images[indices.imageIdx].uid;

      const studyInstanceUID = this.studyInfoDicom.StudyInstanceUID;
      const seriesInstanceUID
        = this.imageInfoDicom[sopInstanceUID].SeriesInstanceUID;

      return { studyInstanceUID, seriesInstanceUID, sopInstanceUID };
    },

    // vtk images
    addVtkImageData(sopInstanceUID: string, imageData: vtkImageData) {
      if (!sopInstanceUID || !imageData) return;

      const metaData: ImageMetadata = {
        name: sopInstanceUID,
        dimensions: imageData.getDimensions(),
        spacing: imageData.getSpacing(),
        origin: imageData.getOrigin(),
        orientation: imageData.getDirection(),
        lpsOrientation: getLPSDirections(imageData.getDirection()),
        worldBounds: imageData.getBounds(),
        worldToIndex: imageData.getWorldToIndex(),
        indexToWorld: imageData.getIndexToWorld(),
      };
      // console.log('DebugMeta name', metaData.name);
      // console.log('DebugMeta dimensions', metaData.dimensions);
      // console.log('DebugMeta spacing', metaData.spacing);
      // console.log('DebugMeta origin', metaData.origin);
      // console.log('DebugMeta orientation', metaData.orientation);
      // console.log('DebugMeta lpsOrientation', metaData.lpsOrientation);
      // console.log('DebugMeta worldBounds', metaData.worldBounds);
      // console.log('DebugMeta worldToIndex', metaData.worldToIndex);
      // console.log('DebugMeta indexToWorld', metaData.indexToWorld);

      if (this.vtkImageDataFiFo) {
        this.vtkImageDataFiFo.add(sopInstanceUID, { imageData, metaData });
        // this.$proxies.addData(sopInstanceUID, imageData);
      }
    },
    modifyVtkImageData(
      sopInstanceUID: string,
      pixelData:
        | Uint8Array
        | Uint16Array
        | Uint32Array
        | Float32Array
        | Float64Array,
      changeDescription: string,
    ) {
      // Early validation
      if (!sopInstanceUID || !pixelData) {
        console.warn('modifyVtkImageData: Missing sopInstanceUID or pixelData');
        return false;
      }
      if (!this.vtkImageDataFiFo) {
        console.warn('modifyVtkImageData: vtkImageDataFiFo is not initialized');
        return false;
      }
      // Get existing imageData AND metaData from cache
      const cached = this.vtkImageDataFiFo.get(sopInstanceUID);
      if (!cached) {
        console.warn(
          `modifyVtkImageData: No image found for ${sopInstanceUID}`,
        );
        return false;
      }

      const { imageData } = cached;

      // Validate dimensions
      const dimensions = imageData.getDimensions();
      const expectedLength = dimensions[0] * dimensions[1] * dimensions[2];

      if (pixelData.length !== expectedLength) {
        console.error(
          `modifyVtkImageData: Pixel data length mismatch. `
          + `Expected ${expectedLength} (${dimensions[0]}x${dimensions[1]}x${dimensions[2]}), `
          + `got ${pixelData.length}`,
        );
        return false;
      }

      // Create a deep copy of the imageData to avoid mutating the original
      const newImageData = vtkImageData.newInstance();
      newImageData.setOrigin(imageData.getOrigin());
      newImageData.setSpacing(imageData.getSpacing());
      newImageData.setDirection(imageData.getDirection());
      newImageData.setDimensions(dimensions);

      if (!this.originalVtkImageData[sopInstanceUID]) {
        this.originalVtkImageData[sopInstanceUID] = {
          description: changeDescription,
          originalData: imageData,
        };
      }

      // Set the new pixel data
      const scalars = imageData.getPointData().getScalars();
      const newScalars = scalars.newClone();
      newScalars.setData(pixelData);
      newImageData.getPointData().setScalars(newScalars);

      this.$proxies.deleteData(sopInstanceUID);
      this.$proxies.addData(sopInstanceUID, newImageData);

      // Update the cache with the new imageData and preserved metaData
      cached.imageData = newImageData;

      // Optional: Track the change
      console.log(
        `modifyVtkImageData: ${changeDescription} for ${sopInstanceUID}`,
      );

      return true;
    },
    resetModifiedVtkImageData(sopInstanceUIDs: string[]) {
      if (!this.vtkImageDataFiFo) return;
      sopInstanceUIDs.forEach((uid) => {
        const cached = this.vtkImageDataFiFo?.get(uid);
        if (!cached) return;
        const original = this.originalVtkImageData[uid];
        if (!original?.originalData) return;
        cached.imageData = original.originalData;
        delete this.originalVtkImageData[uid];

        this.$proxies.deleteData(uid);
        this.$proxies.addData(uid, cached.imageData);
      });
    },

    addOverlayVtkImageData(
      sopInstanceUID: string,
      pixelData: Uint8Array,
      description: string = '',
    ): boolean {
      if (!sopInstanceUID || !pixelData) {
        console.warn(
          'addOverlayVtkImageData: Missing sopInstanceUID or pixelData',
        );
        return false;
      }
      if (!this.vtkImageDataFiFo) {
        console.warn(
          'addOverlayVtkImageData: vtkImageDataFiFo is not initialized',
        );
        return false;
      }

      const cached = this.vtkImageDataFiFo.get(sopInstanceUID);
      if (!cached) {
        console.warn(
          `addOverlayVtkImageData: No base image found for ${sopInstanceUID}`,
        );
        return false;
      }

      const { imageData } = cached;
      const dimensions = imageData.getDimensions();
      const expectedLength = dimensions[0] * dimensions[1] * dimensions[2] * 4;

      if (pixelData.length !== expectedLength) {
        console.error(
          `addOverlayVtkImageData: Pixel data length mismatch. `
          + `Expected ${expectedLength} (${dimensions[0]}x${dimensions[1]}x${dimensions[2]}x4 RGBA), `
          + `got ${pixelData.length}`,
        );
        return false;
      }

      const overlay = vtkImageData.newInstance();
      const origin = imageData.getOrigin();
      const dir = imageData.getDirection();
      // K-axis is 3rd column of direction matrix — offset toward camera
      const epsilon = 0.01;
      overlay.setOrigin([
        origin[0] - dir[6] * epsilon,
        origin[1] - dir[7] * epsilon,
        origin[2] - dir[8] * epsilon,
      ]);
      overlay.setSpacing(imageData.getSpacing());
      overlay.setDirection(dir);
      overlay.setExtent(imageData.getExtent());

      const scalars = vtkDataArray.newInstance({
        name: 'OverlayRGBA',
        numberOfComponents: 4,
        values: pixelData,
      });
      overlay.getPointData().setScalars(scalars);

      this.removeOverlayVtkImageData(sopInstanceUID);

      this.overlayVtkImageData[sopInstanceUID] = {
        description,
        overlayData: overlay,
      };

      return true;
    },
    removeOverlayVtkImageData(sopInstanceUID: string) {
      if (!sopInstanceUID || !this.overlayVtkImageData[sopInstanceUID]) return;
      delete this.overlayVtkImageData[sopInstanceUID];
      const overlayID = `overlay:${sopInstanceUID}`;
      if (this.$proxies.getData(overlayID)) {
        this.$proxies.deleteData(overlayID);
      }
    },

    getOverlayVtkImageData(sopInstanceUID: string): vtkImageData | null {
      if (!sopInstanceUID) return null;
      const cached = this.vtkImageDataFiFo?.get(sopInstanceUID);
      if (!cached?.imageData) return null;

      if (this.overlayVtkImageData[sopInstanceUID]) {
        return this.overlayVtkImageData[sopInstanceUID].overlayData;
      }
      return null;

      // const src = cached.imageData;
      // const dims = src.getDimensions();
      // const w = dims[0];
      // const h = dims[1];

      // const overlay = vtkImageData.newInstance();
      // const origin = src.getOrigin();
      // const dir = src.getDirection();
      // // K-axis is 3rd column of direction matrix — offset toward camera
      // const epsilon = 0.01;
      // overlay.setOrigin([
      //   origin[0] - dir[6] * epsilon,
      //   origin[1] - dir[7] * epsilon,
      //   origin[2] - dir[8] * epsilon,
      // ]);
      // overlay.setSpacing(src.getSpacing());
      // overlay.setDirection(dir);
      // overlay.setExtent(src.getExtent());

      // const numPixels = w * h;
      // const rgba = new Uint8Array(numPixels * 4);

      // // Debug pattern: 8×8 pixel checkerboard, red vs blue, 50% alpha
      // const blockSize = 8;
      // for (let j = 0; j < h; j++) {
      //   for (let i = 0; i < w; i++) {
      //     const idx = (j * w + i) * 4;
      //     const checker = (Math.floor(i / blockSize) + Math.floor(j / blockSize)) % 2;
      //     if (checker === 0) {
      //       rgba[idx] = 255; // R
      //       rgba[idx + 1] = 0;   // G
      //       rgba[idx + 2] = 0;   // B
      //       rgba[idx + 3] = 64; // A
      //     } else {
      //       rgba[idx] = 0;
      //       rgba[idx + 1] = 0;
      //       rgba[idx + 2] = 255;
      //       rgba[idx + 3] = 64;
      //     }
      //   }
      // }

      // const scalars = vtkDataArray.newInstance({
      //   name: 'OverlayRGBA',
      //   numberOfComponents: 4,
      //   values: rgba,
      // });
      // overlay.getPointData().setScalars(scalars);

      // return overlay;
    },
    registerOverlayDataProxy(sopInstanceUID: string): string | null {
      if (!sopInstanceUID) return null;
      const overlayID = `overlay:${sopInstanceUID}`;
      if (this.$proxies.getData(overlayID)) return overlayID;

      const overlay = this.getOverlayVtkImageData(sopInstanceUID);
      if (!overlay) return null;

      this.$proxies.addData(overlayID, overlay);
      return overlayID;
    },

    imageAvailableForRendering(sopInstanceUID: string) {
      if (!sopInstanceUID) return false;
      if (this.$proxies.getData(sopInstanceUID)) {
        return true;
      }
      if (this.imageAvailable(sopInstanceUID)) {
        if (this.vtkImageDataFiFo) {
          const imageData
            = this.vtkImageDataFiFo.get(sopInstanceUID)?.imageData;
          if (imageData) {
            // checks if already added to proxies
            this.$proxies.addData(sopInstanceUID, imageData);
          }
        }
        return true;
      }
      return false;
    },

    imageAvailableIndex(indices: ImageIndices) {
      const sopInstanceUID = this.getImageUID(indices);
      if (!sopInstanceUID) return false;
      if (this.vtkImageDataFiFo) {
        return this.vtkImageDataFiFo.get(sopInstanceUID) !== undefined;
      }
      return false;
    },
    imageAvailable(sopInstanceUID: string) {
      if (this.vtkImageDataFiFo) {
        return this.vtkImageDataFiFo.get(sopInstanceUID) !== undefined;
      }
      return undefined;
    },
    isImageAvailable(sopInstanceUID: string): boolean {
      // function is similar to imageAvailable but return boolean
      if (!sopInstanceUID) return false;

      // Check if the image data is already available
      if (this.imageAvailable(sopInstanceUID)) {
        return true;
      }

      return false;
    },
    getDefautImageMetadata() {
      return defaultImageMetadata();
    },

    getSliceKeyFromUID(sopInstanceUID: string) {
      const sliceNormal
        = this.imageInfoDicom[sopInstanceUID]?.ImageNormalVector;

      if (!sliceNormal) return undefined;

      const normAsString = sliceNormal
        .map((coord) => {
          // Round first
          const rounded = Math.round(coord * 1e4) / 1e4;

          // Convert to string while removing unnecessary trailing zeros
          let str = rounded.toFixed(4).replace(/\.?0+$/, '');

          // Ensure at least one decimal place
          if (!str.includes('.')) {
            str += '.0';
          }

          return str;
        })
        .join('_');

      return normAsString;
    },

    getVtkImageDataIndex(indices: ImageIndices, viewID: string) {
      const sopInstanceUID = this.getImageUID(indices);

      if (!sopInstanceUID) return undefined;
      return this.getVtkImageData(sopInstanceUID, viewID);
    },
    async createVtkImageDataInstance(sopInstanceUID: string) {
      if (!sopInstanceUID) return undefined;
      const existing
        = this._inflightCreateVtkImageDataInstances[sopInstanceUID];
      if (existing !== undefined) return existing;
      const promise = this._doCreateVtkImageDataInstance(sopInstanceUID);
      this._inflightCreateVtkImageDataInstances[sopInstanceUID] = promise;
      try {
        return await promise;
      }
      finally {
        delete this._inflightCreateVtkImageDataInstances[sopInstanceUID];
      }
    },
    async _doCreateVtkImageDataInstance(sopInstanceUID: string) {
      if (!sopInstanceUID) return undefined;

      // if image is requested, fetch palette color lookup table
      // (will check if not present or already fetched)
      await this.fetchPaletteColorLookupTable(sopInstanceUID);

      const orthancStore = useOrthancStore();
      const orthancInstanceId
        = this.imageInfoDicom[sopInstanceUID].OrthancInstanceId;
      if (!orthancInstanceId) return undefined;

      let useRawPixelData = true;
      // check transfer syntax is UNCOMPRESSED_LITTLE_ENDIAN_TRANSFER_SYNTAXES
      const transferSyntax
        = this.imageInfoDicom[sopInstanceUID].TransferSyntaxUID;
      if (
        !UNCOMPRESSED_LITTLE_ENDIAN_TRANSFER_SYNTAXES.includes(transferSyntax)
      ) {
        useRawPixelData = false;
      }
      const tail = sopInstanceUID.split('-').pop();
      const frame = tail ? Number.parseInt(tail, 10) - 1 : 0;

      // to construct vtkImageData
      type ScalarArray
        = | Int8Array
          | Uint8Array
          | Int16Array
          | Uint16Array
          | Int32Array
          | Uint32Array
          | Float32Array;

      let scalars: ScalarArray;
      if (useRawPixelData) {
        try {
          const framePixelData = await orthancStore.fetchFrameRaw(
            orthancInstanceId,
            frame,
          );

          // use fetchFrameRawDicomWeb instead of fetchFrameRaw
          //
          // const orthancStore = useOrthancStore();
          // const orthancInstanceId = this.imageInfoDicom[sopInstanceUID].OrthancInstanceId;
          // if (!orthancInstanceId) return undefined;
          // const info = this.imageInfoDicom[sopInstanceUID];
          // if (!info) return undefined;

          // let useRawPixelData = true;
          // const transferSyntax = info.TransferSyntaxUID;
          // if (!UNCOMPRESSED_LITTLE_ENDIAN_TRANSFER_SYNTAXES.includes(transferSyntax)) {
          //   useRawPixelData = false;
          // }
          // const [cleanSop, framePart] = sopInstanceUID.split('-');
          // const frame = framePart ? parseInt(framePart, 10) - 1 : 0;

          // // to construct vtkImageData
          // let scalars: ArrayBufferView;
          // if (useRawPixelData) {
          //   try {
          //     const framePixelData = await orthancStore.fetchFrameRawDicomWeb(
          //       this.studyInfoDicom.StudyInstanceUID,
          //       info.SeriesInstanceUID,
          //       cleanSop,
          //       frame
          //     );

          if (!framePixelData) return undefined;

          const voxels = framePixelData;

          const bits = Number(
            this.imageInfoDicom[sopInstanceUID].BitsAllocated ?? 16,
          );
          const VALID_BITS_ALLOCATED = [1, 8, 16, 32];
          if (!VALID_BITS_ALLOCATED.includes(bits)) {
            throw new Error(
              `Invalid BitsAllocated=${bits}; DICOM allows 1, 8, 16, or 32`,
            );
          }
          const signed
            = Number(
              this.imageInfoDicom[sopInstanceUID].PixelRepresentation ?? 0,
            ) === 1;

          function makeTyped(
            this: void,
            buffer: ArrayBuffer,
            bitsLocal: number,
            signedLocal: boolean,
          ): ScalarArray {
            if (bitsLocal <= 8) {
              return signedLocal
                ? new Int8Array(buffer)
                : new Uint8Array(buffer);
            }
            if (bitsLocal <= 16) {
              return signedLocal
                ? new Int16Array(buffer)
                : new Uint16Array(buffer);
            }
            if (bitsLocal <= 32) {
              return signedLocal
                ? new Int32Array(buffer)
                : new Uint32Array(buffer);
            }
            throw new Error(`Unsupported BitsAllocated=${bitsLocal}`);
          };

          // if (voxels instanceof ArrayBuffer) {
          scalars = makeTyped(voxels, bits, signed);
          // }
          // else if (ArrayBuffer.isView(voxels)) {
          //   scalars = voxels;
          // }
          // else {
          //   throw new TypeError(`Unsupported voxel buffer type ${voxels.constructor.name}`);
          // }
        }
        catch (error) {
          console.error('Failed to fetch/decode frame:', sopInstanceUID, error);
          return undefined;
        }
      }
      else {
        function getNpyInfo(buffer: ArrayBuffer): {
          offset: number;
          descr: string;
        } {
          if (buffer.byteLength < 10)
            throw new Error('Invalid .npy file: too short');
          const view = new DataView(buffer);
          if (view.getUint8(0) !== 0x93) throw new Error('Invalid .npy magic');
          const major = view.getUint8(6);
          let offset;
          if (major === 1) {
            offset = 10 + view.getUint16(8, true);
          }
          else if (major === 2) {
            offset = 12 + view.getUint32(8, true);
          }
          else {
            throw new Error(`Unsupported .npy version: ${major}`);
          }
          const headerStart = major === 1 ? 10 : 12;
          const header = new TextDecoder().decode(
            buffer.slice(headerStart, offset),
          );
          const descr = header.match(/'descr'\s*:\s*'([^']+)'/)?.[1];
          if (!descr) throw new Error('Could not parse dtype from .npy header');
          return { offset, descr };
        }

        function npyToFloat32(buffer: ArrayBuffer): Float32Array {
          const { offset, descr } = getNpyInfo(buffer);
          const len = buffer.byteLength - offset;
          switch (descr) {
            case '<f4':
              return new Float32Array(buffer, offset, len / 4);
            case '<i2':
              return Float32Array.from(new Int16Array(buffer, offset, len / 2));
            case '<u2':
              return Float32Array.from(
                new Uint16Array(buffer, offset, len / 2),
              );
            case '<u1':
              return Float32Array.from(new Uint8Array(buffer, offset, len));
            default:
              throw new Error(`Unhandled DICOM pixel dtype: ${descr}`);
          }
        }

        try {
          const framePixelData = await orthancStore.fetchFrameNumpy(
            orthancInstanceId,
            frame,
            false,
          );
          if (!framePixelData) return undefined;
          scalars = npyToFloat32(framePixelData);
        }
        catch (error) {
          console.error(
            'Failed to fetch/decode frame (numpy):',
            sopInstanceUID,
            error,
          );
          return undefined;
        }
      }

      function allocateSameType(src: ScalarArray, length: number): ScalarArray {
        if (src instanceof Int8Array) return new Int8Array(length);
        if (src instanceof Uint8Array) return new Uint8Array(length);
        if (src instanceof Int16Array) return new Int16Array(length);
        if (src instanceof Uint16Array) return new Uint16Array(length);
        if (src instanceof Int32Array) return new Int32Array(length);
        if (src instanceof Uint32Array) return new Uint32Array(length);
        if (src instanceof Float32Array) return new Float32Array(length);

        throw new Error('Unsupported scalar type');
      }

      function planarToInterleaved(src: ScalarArray, numComps: number) {
        const n = src.length / numComps;
        const out = allocateSameType(src, src.length);
        for (let i = 0; i < n; i++) {
          for (let c = 0; c < numComps; c++) {
            out[i * numComps + c] = src[c * n + i];
          }
        }
        return out;
      }

      const numComps = this.imageInfoDicom[sopInstanceUID].SamplesPerPixel;
      // Convert Planar Configuration 1 (planar) → interleaved for VTK
      const planarConfig = Number(
        this.imageInfoDicom[sopInstanceUID].PlanarConfiguration ?? 0,
      );
      if (numComps > 1 && planarConfig === 1 && scalars) {
        scalars = planarToInterleaved(scalars, numComps);
      }

      // Optional rescale to Float32 (HU, etc.)
      const slope = Number(
        this.imageInfoDicom[sopInstanceUID].RescaleSlope ?? 1,
      );
      const intercept = Number(
        this.imageInfoDicom[sopInstanceUID].RescaleIntercept ?? 0,
      );
      if (slope !== 1 || intercept !== 0) {
        const inArr = scalars;
        const n = inArr.length;
        const out = new Float32Array(n);
        const src = inArr;
        for (let i = 0; i < n; i++) {
          out[i] = src[i] * slope + intercept;
        }
        scalars = out;
      }

      // create vtkImageData
      const rows = this.imageInfoDicom[sopInstanceUID].Rows;
      const cols = this.imageInfoDicom[sopInstanceUID].Columns;

      try {
        // --- Spacing (vtk expects [dx, dy, dz]) ---
        // DICOM PixelSpacing is [row, col] => map to [col, row]
        const [rowSp, colSp] = (
          this.imageInfoDicom[sopInstanceUID].PixelSpacing || [1, 1]
        ).map(Number);
        const dz
          = Number(this.imageInfoDicom[sopInstanceUID].SliceThickness ?? 1) || 1;

        // --- Origin (center of pixel at i=0,j=0,k=0) ---
        const origin = (
          this.imageInfoDicom[sopInstanceUID].ImagePositionPatient || [0, 0, 0]
        ).map(Number);

        // --- Direction matrix from ImageOrientationPatient ---
        // IOP = [rowCosines(3), colCosines(3)]
        const iop = (
          this.imageInfoDicom[sopInstanceUID].ImageOrientationPatient || [
            1,
            0,
            0,
            0,
            1,
            0,
          ]
        ).map(Number);
        const r = iop.slice(0, 3);
        const c = iop.slice(3, 6);

        // cross(r, c) -> slice normal (k axis)
        const nRaw = this.imageInfoDicom[sopInstanceUID].ImageNormalVector || [
          r[1] * c[2] - r[2] * c[1],
          r[2] * c[0] - r[0] * c[2],
          r[0] * c[1] - r[1] * c[0],
        ];

        const norm = (v: number[]) => {
          const m = Math.hypot(v[0], v[1], v[2]) || 1;
          return [v[0] / m, v[1] / m, v[2] / m];
        };
        const R = norm(r);
        const C = norm(c);
        const N = norm(nRaw);

        // vtk.js setDirection wants rows = directions of I, J, K axes in world coords
        // I = columns -> row cosines, J = rows -> column cosines, K = slice normal
        const direction = [
          R[0],
          R[1],
          R[2],
          C[0],
          C[1],
          C[2],
          N[0],
          N[1],
          N[2],
        ];

        // Build vtkImageData
        const image = vtkImageData.newInstance();
        image.setExtent(0, cols - 1, 0, rows - 1, 0, 0);
        image.setSpacing([Number(colSp) || 1, Number(rowSp) || 1, dz] as [
          number,
          number,
          number,
        ]);
        image.setOrigin(origin as [number, number, number]);
        image.setDirection(new Float32Array(direction));

        const dataArray = vtkDataArray.newInstance({
          name: 'Scalars',
          numberOfComponents: numComps,
          values: scalars,
        });
        image.getPointData().setScalars(dataArray);

        // console.log('createVtkImageDataInstance debug', {
        //   sopInstanceUID,
        //   rows, cols, numComps, bits, signed,
        //   extent: image.getExtent(),
        //   spacing: image.getSpacing(),
        //   origin: image.getOrigin(),
        //   direction: Array.from(image.getDirection()),
        //   scalarType: scalars.constructor.name,
        //   scalarLength: scalars.byteLength,
        //   expectedPixels: rows * cols * numComps,
        //   actualPixels: (scalars as any).length,
        //   dataRange: image.getPointData().getScalars().getRange(),
        // });

        return image;
      }
      catch (error) {
        console.error(
          'createVtkImageDataInstance: failed to build vtkImageData',
          sopInstanceUID,
          error,
        );
        return undefined;
      }
    },
    _removeFromFetchQueue(sopInstanceUID: string, viewID: string) {
      const q = this.fetchQueue[viewID];
      if (q) {
        const idx = q.indexOf(sopInstanceUID);
        if (idx !== -1) q.splice(idx, 1);
        if (q.length === 0) delete this.fetchQueue[viewID];
      }
    },
    _removeFromBackgroundQueue(sopInstanceUID: string) {
      const idx = this.backgroundQueue.indexOf(sopInstanceUID);
      if (idx !== -1) this.backgroundQueue.splice(idx, 1);
    },

    async loadVtkImageAndMetaData(
      sopInstanceUID: string,
      viewID: string = '',
      backgroundFetchRequested: boolean = false,
    ) {
      const backgroundFetch = backgroundFetchRequested || !viewID;

      if (this.imageAvailable(sopInstanceUID)) {
        if (backgroundFetch) {
          this._removeFromBackgroundQueue(sopInstanceUID);
        }
        else {
          this._removeFromFetchQueue(sopInstanceUID, viewID);
        }
        return;
      }

      if (backgroundFetch) {
        if (this.priorityQueueLength() > 0) {
          this.addToBackgroundQueue(sopInstanceUID);
          return;
        }
        if (this.fetchConcurrencyCount >= this.fetchConcurrencyLimit) {
          this.addToBackgroundQueue(sopInstanceUID);
          return;
        }
      }
      else {
        // add, might not be in queue yet
        if (!this.addToFetchQueue(sopInstanceUID, viewID)) {
          setTimeout(() => this.processPriorityQueue(), 0);
          return;
        }
        if (this.fetchConcurrencyCount >= this.fetchConcurrencyLimit) {
          return;
        }
      }

      try {
        if (backgroundFetch) {
          this.backgroundFetchConcurrencyCount++;
        }
        else {
          this.fetchConcurrencyCount++;
        }
        const image = await this.createVtkImageDataInstance(sopInstanceUID);
        if (image) {
          this.addVtkImageData(sopInstanceUID, image);
        }
      }
      catch (error) {
        console.error(
          'Failed to create vtk image data instance:',
          error,
          sopInstanceUID,
        );
      }
      finally {
        if (backgroundFetch) {
          this.backgroundFetchConcurrencyCount--;
          this._removeFromBackgroundQueue(sopInstanceUID);
        }
        else {
          this.fetchConcurrencyCount--;
          this._removeFromFetchQueue(sopInstanceUID, viewID);
        }
        setTimeout(() => this.processPriorityQueue(), 0);
      }
    },
    async fetchOrGetVtkImageData(sopInstanceUID: string) {
      // This function contains similar code to loadVtkImageAndMetaData on purpose.
      //  It ensures that it will return image
      if (!sopInstanceUID) return undefined;

      // Check if the image data is already available
      if (this.vtkImageDataFiFo) {
        const imageAndMetaData = this.vtkImageDataFiFo.get(sopInstanceUID);

        if (imageAndMetaData) {
          return imageAndMetaData.imageData;
        }
      }
      // If not available, load the image data
      try {
        const image = await this.createVtkImageDataInstance(sopInstanceUID);
        if (image) {
          this.addVtkImageData(sopInstanceUID, image);
          return image;
        }
      }
      catch (error) {
        console.error(
          'Failed to create vtk image data instance:',
          error,
          sopInstanceUID,
        );
      }
      return undefined;
    },
    getVtkImageData(sopInstanceUID: string, viewID: string = '') {
      if (!sopInstanceUID) return undefined;
      if (this.vtkImageDataFiFo) {
        const imageAndMetaData = this.vtkImageDataFiFo.get(sopInstanceUID);
        if (!imageAndMetaData) {
          this.loadVtkImageAndMetaData(sopInstanceUID, viewID)
            .catch ((error) => {
              console.error(
                'Failed to load image getVtkImageData:',
                error,
                sopInstanceUID,
              );
            });
          return undefined;
        }
        this.$proxies.addData(sopInstanceUID, imageAndMetaData.imageData);
        return imageAndMetaData.imageData;
      }
      return undefined;
    },

    addToFetchQueue(sopInstanceUID: string, viewID: string = '') {
      if (this.imageAvailable(sopInstanceUID)) return false;
      if (!viewID) return false;
      if (this.fetchQueue[viewID]?.includes(sopInstanceUID)) return false;

      if (!this.fetchQueue[viewID]) {
        this.fetchQueue[viewID] = [];
      }
      this.fetchQueue[viewID].push(sopInstanceUID);

      if (!this.fetchQueueTimer) {
        this.fetchQueueTimer = window.setInterval(() => {
          this.processPriorityQueue();
        }, this.fetchTimerInterval);
      }
      return true;
    },

    isFetchingImage(viewID: string) {
      const q = this.fetchQueue[viewID];
      if (!q || q.length === 0) return false;
      return true;
    },

    processPriorityQueue() {
      console.log(
        'processPriorityQueue',
        this.priorityQueueLength(),
        this.backgroundQueueLength(),
      );
      if (this.priorityQueueLength() === 0) {
        if (this.fetchQueueTimer) {
          window.clearInterval(this.fetchQueueTimer);
          this.fetchQueueTimer = null;
        }
        return;
      }
      if (this.fetchConcurrencyCount >= this.fetchConcurrencyLimit) return;

      let stoppedAtViewID: string | null = null;

      for (const viewID of Object.keys(this.fetchQueue)) {
        if (this.fetchConcurrencyCount >= this.fetchConcurrencyLimit) {
          stoppedAtViewID = viewID;
          break;
        }

        const q = this.fetchQueue[viewID];
        if (!q || q.length === 0) continue;

        const sopInstanceUID = q[0];
        if (!sopInstanceUID) continue;
        // Skip if already being fetched — prevents ghost concurrency inflation
        if (sopInstanceUID in this._inflightCreateVtkImageDataInstances) {
          continue;
        }
        void this.loadVtkImageAndMetaData(sopInstanceUID, viewID);
      }

      if (stoppedAtViewID) {
        const keys = Object.keys(this.fetchQueue);
        const idx = keys.indexOf(stoppedAtViewID);
        if (idx > 0) {
          const reordered: Record<string, string[]> = {};
          for (let i = idx; i < keys.length; i++) {
            reordered[keys[i]] = this.fetchQueue[keys[i]];
          }
          for (let i = 0; i < idx; i++) {
            reordered[keys[i]] = this.fetchQueue[keys[i]];
          }
          this.fetchQueue = reordered;
        }
      }
    },

    addToBackgroundQueue(sopInstanceUID: string) {
      if (this.imageAvailable(sopInstanceUID)) return false;
      if (this.backgroundQueue.includes(sopInstanceUID)) return false;

      this.backgroundQueue.push(sopInstanceUID);

      if (!this.backgroundQueueTimer) {
        this.backgroundQueueTimer = window.setInterval(() => {
          this.processBackgroundQueue();
        }, this.backgroundQueueInterval);
      }
      return true;
    },

    addSliceToBackgroundQueue(indices: ImageIndices) {
      const sliceImageUIDs = this.getSliceImageUIDs(
        indices.seriesIdx,
        indices.sliceIdx,
      );
      if (!sliceImageUIDs) return;
      this.addToBackgroundQueueArray(sliceImageUIDs);
    },

    addToBackgroundQueueArray(uids: string[]) {
      if (!Array.isArray(uids) || uids.length === 0) return;

      for (const uid of uids) {
        if (uid) this.addToBackgroundQueue(uid);
      }
    },

    prioritizeBackgroundQueue(sopInstanceUID: string) {
      if (this.imageAvailable(sopInstanceUID)) return false;
      this._removeFromBackgroundQueue(sopInstanceUID);
      this.backgroundQueue.unshift(sopInstanceUID);

      if (!this.backgroundQueueTimer) {
        this.backgroundQueueTimer = window.setInterval(() => {
          this.processBackgroundQueue();
        }, this.backgroundQueueInterval);
      }
      return true;
    },

    processBackgroundQueue() {
      if (this.backgroundQueue.length === 0) {
        if (this.backgroundQueueTimer) {
          window.clearInterval(this.backgroundQueueTimer);
          this.backgroundQueueTimer = null;
        }
        return;
      }
      if (this.priorityQueueLength() > 0) return;

      const available
        = this.backgroundFetchConcurrencyLimit
          - this.backgroundFetchConcurrencyCount;
      if (available <= 0) return;

      const batch = this.backgroundQueue.splice(0, available);
      batch.forEach((uid) => {
        this.loadVtkImageAndMetaData(uid, '', true).catch(() => {});
      });
    },

    clearBackgroundQueue() {
      this.backgroundQueue = [];
      if (this.backgroundQueueTimer) {
        window.clearInterval(this.backgroundQueueTimer);
        this.backgroundQueueTimer = null;
      }
    },

    getVtkMetaDataIndex(indices: ImageIndices) {
      const sopInstanceUID = this.getImageUID(indices);
      if (!sopInstanceUID) return defaultImageMetadata();
      for (let lookahead = 1; lookahead < 11; ++lookahead) {
        const lookaheadIndices = {
          ...indices,
          imageIdx: indices.imageIdx + lookahead,
        };
        const lookaheadSopInstanceUID = this.getImageUID(lookaheadIndices);
        // console.log({ lookahead, lookaheadIndices, lookaheadSopInstanceUID });
        if (lookaheadSopInstanceUID)
          this.prioritizeBackgroundQueue(lookaheadSopInstanceUID);
      }
      return this.getVtkMetaData(sopInstanceUID);
    },
    getVtkMetaData(sopInstanceUID: string) {
      if (!sopInstanceUID) return defaultImageMetadata();
      if (this.vtkImageDataFiFo) {
        const imageAndMetaData = this.vtkImageDataFiFo.get(sopInstanceUID);
        if (!imageAndMetaData) return defaultImageMetadata();
        return imageAndMetaData.metaData;
      }
      return defaultImageMetadata();
    },
    async loadVtkImageAndMetaDataSlice(
      seriesIndex: number,
      sliceIndex: number,
    ) {
      const series = this.activeStudy.series[seriesIndex];
      const sopInstanceUIDs = series.slices[sliceIndex].images.map(
        image => image.uid,
      );

      sopInstanceUIDs.forEach((uid) => {
        void this.loadVtkImageAndMetaData(uid);
      });
    },

    // init  and sorting
    populateActiveStudy() {
      const seriesArray: Series[] = this.seriesDicom.map((seriesUID) => {
        const images
          = this.seriesImagesDicom[seriesUID]?.map((imageUID) => {
            const imageInfo = this.imageInfoDicom[imageUID];
            return { uid: imageInfo.SOPInstanceUID };
          }) || [];

        // Group images by calculated SliceLocation
        const slicesMap: Record<string, Image[]> = {};
        images.forEach((image) => {
          const sliceLocation
            = this.imageInfoDicom[image.uid].CalculatedSliceLocation;
          if (!slicesMap[sliceLocation]) {
            slicesMap[sliceLocation] = [];
          }
          slicesMap[sliceLocation].push(image);
        });

        // Convert slicesMap to slices array
        const slices: Slice[] = Object.keys(slicesMap).map((sliceLocation) => {
          return { images: slicesMap[sliceLocation] };
        });

        return { uid: seriesUID, slices };
      });

      this.activeStudy.series = seriesArray;
    },

    sortActiveStudy(options?: SortingOptions) {
      if (!this.activeStudy.series.length) {
        console.warn('No series to sort in activeStudy.');
        return;
      }

      const sorter = new StudySorter(
        {
          seriesInfoDicom: this.seriesInfoDicom,
          imageInfoDicom: this.imageInfoDicom,
          seriesImagesDicom: this.seriesImagesDicom,
          studyInfoDicom: this.studyInfoDicom,
        },
        options,
      );

      const { series, newSeriesInfo, sliceTimes } = sorter.sort(this.activeStudy.series);
      this.activeStudy.series = series;
      Object.assign(this.seriesInfoDicom, newSeriesInfo);

      // Write computed slice-time deltas back into imageInfoDicom
      Object.entries(sliceTimes).forEach(([uid, bySeriesIndex]) => {
        const info = this.imageInfoDicom[uid];
        if (!info) return;
        if (!info.CalculatedSliceTime) info.CalculatedSliceTime = {};
        Object.assign(info.CalculatedSliceTime, bySeriesIndex);
      });
    },
    populateAndSortActiveStudy() {
      // Populate activeStudy
      this.populateActiveStudy();

      // Ensure there are series to sort
      if (!this.activeStudy.series.length) {
        console.warn('No series to sort in activeStudy.');
        return;
      }

      // Sort activeStudy
      this.sortActiveStudy();

      // console.log('SortedActiveStudy', this.activeStudy);

      // Initialize or reset indices for all images
      Object.values(this.imageInfoDicom).forEach((imageInfo) => {
        imageInfo.seriesIndices = [];
      });

      // Add all indices for each image after sorting
      this.activeStudy.series.forEach((series, seriesIndex) => {
        series.slices.forEach((slice) => {
          slice.images.forEach((image) => {
            if (this.imageInfoDicom[image.uid]) {
              if (!this.imageInfoDicom[image.uid].seriesIndices) {
                this.imageInfoDicom[image.uid].seriesIndices = [];
              }
              this.imageInfoDicom[image.uid].seriesIndices?.push(seriesIndex);
            }
          });
        });
      });
    },
  },
});
